// Utility functions

// Generate unique PNR (Passenger Name Record)
export const generatePNR = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let pnr = '';
  for (let i = 0; i < 10; i++) {
    pnr += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pnr;
};

// Calculate refund (75% if cancelled more than 24hrs before, 50% if 24-12 hrs, 0% if less than 12 hrs)
export const calculateRefund = (fare, hoursBeforeDeparture = 48) => {
  if (hoursBeforeDeparture >= 24) {
    return Math.floor(fare * 0.75);
  } else if (hoursBeforeDeparture >= 12) {
    return Math.floor(fare * 0.5);
  }
  return 0;
};

// Format date
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date.toDate ? date.toDate() : date);
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Format time
export const formatTime = (time) => {
  if (!time) return '';
  const date = new Date(time.toDate ? time.toDate() : time);
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format currency (Indian Rupees)
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

// Calculate journey duration
export const calculateDuration = (departureTime, arrivalTime) => {
  const departure = new Date(departureTime.toDate ? departureTime.toDate() : departureTime);
  const arrival = new Date(arrivalTime.toDate ? arrivalTime.toDate() : arrivalTime);
  const diff = arrival - departure;
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  return `${hours}h ${minutes}m`;
};

// Validate email
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Validate phone number (Indian)
export const validatePhoneNumber = (phone) => {
  const re = /^[6-9]\d{9}$/;
  return re.test(phone);
};

// Validate PNR format
export const validatePNR = (pnr) => {
  return /^[A-Z0-9]{10}$/.test(pnr);
};

// Check if time is valid for boarding (within 4 hours of departure)
export const canBoard = (departureTime) => {
  const now = new Date();
  const departure = new Date(departureTime.toDate ? departureTime.toDate() : departureTime);
  const timeDiff = departure - now;
  const hoursDiff = timeDiff / 3600000;
  return hoursDiff >= 0 && hoursDiff <= 4;
};

// Get seat class based on seat number
export const getSeatClass = (seatNumber) => {
  // Example: Seats 1-36 = Window, 37-72 = Aisle
  return seatNumber <= 36 ? 'Window' : 'Aisle';
};

// Generate QR code data
export const generateQRData = (pnr, trainNumber, seatNumber) => {
  return `PNR:${pnr}|TRAIN:${trainNumber}|SEAT:${seatNumber}`;
};

// Calculate age from DOB
export const calculateAge = (dob) => {
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Get passenger category based on age
export const getPassengerCategory = (dob) => {
  const age = calculateAge(dob);
  if (age < 5) return 'Child';
  if (age >= 60) return 'Senior Citizen';
  return 'Adult';
};

// Apply concession based on category
export const calculateFareWithConcession = (baseFare, category) => {
  const concessions = {
    Child: 0.5, // 50% discount
    'Senior Citizen': 0.4, // 40% discount
    Adult: 1.0, // No discount
  };
  return baseFare * (concessions[category] || 1);
};

const helpers = {
  generatePNR,
  calculateRefund,
  formatDate,
  formatTime,
  formatCurrency,
  calculateDuration,
  validateEmail,
  validatePhoneNumber,
  validatePNR,
  canBoard,
  getSeatClass,
  generateQRData,
  calculateAge,
  getPassengerCategory,
  calculateFareWithConcession,
};

export default helpers;
